import React, { useState } from 'react';
import {
  Grid,
  Paper,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  LinearProgress,
  Divider,
} from '@mui/material';
import { harmonateTokens as ht } from '../../theme/harmonateTokens';
import {
  harmonateDashboardPageSx,
  harmonateDashboardTitleSx,
  harmonateKpiCardSx,
  harmonateCardSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateUploadOutlinedSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogCloseIconSx,
  harmonateSelectMenuProps,
  harmonateFormFieldsSx,
} from '../../theme/harmonateStyles';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Help as HelpIcon,
  Error as ErrorIcon,
  CheckCircle as CompletedIcon
} from '@mui/icons-material';
import { Line, Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement);

const AccountsPayableRecovery = () => {
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([
    { name: 'Invoices', size: '2.1MB', status: 'completed' },
    { name: 'ContractualTerms003', size: '3.1MB', status: 'completed' },
    { name: 'NetSuiteQ31925', size: '10.3MB', status: 'uploading', progress: 75 }
  ]);

  const [mappingValues, setMappingValues] = useState({
    accountNumber: 'Auto',
    contract: 'Auto',
    generalLedger: 'NetSuite'
  });

  // Sample data for accounts payable recovery
  const recoveryData = [
    { vendor: 'LMN Company', invoices: 1, amount: 1250, status: 'warning' },
    { vendor: 'QRS Company', invoices: 20, amount: 5000, status: 'success' },
    { vendor: 'TUV Company', invoices: 5, amount: 600, status: 'success' },
    { vendor: 'HIJ Company', invoices: 3, amount: 190, status: 'warning' },
    { vendor: 'JKL Company', invoices: 1, amount: 1400, status: 'success' }
  ];

  // Sample data for monthly recoveries chart
  const monthlyRecoveriesData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Recoveries',
        data: [12, 9, 15, 12, 18, 20],
        backgroundColor: ht.green
      }
    ]
  };

  // Sample data for insights pie chart
  const pieChartData = {
    labels: ['Invoice', 'Fraud'],
    datasets: [
      {
        data: [75, 25],
        backgroundColor: [ht.green, ht.blue],
        borderWidth: 0
      }
    ]
  };

  // Sample data for recent uploads
  const recentUploads = [
    { file: 'ABC Co. Invoice 3 File' },
    { file: 'HIJ Co. PfCap 4 File' },
    { file: 'LMN Co. TRKV File' },
    { file: 'WXU Co. P33 File' }
  ];

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        display: false,
        beginAtZero: true
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: ht.line
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon sx={{ color: ht.green }} />;
      case 'warning':
        return <WarningIcon sx={{ color: '#E6A817' }} />;
      default:
        return null;
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newFiles = files.map(file => ({
      name: file.name,
      size: `${(file.size / (1024 * 1024)).toFixed(1)}MB`,
      status: 'uploading',
      progress: Math.floor(Math.random() * 50) + 30
    }));
    setUploadedFiles(prev => [...prev, ...newFiles]);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    const newFiles = files.map(file => ({
      name: file.name,
      size: `${(file.size / (1024 * 1024)).toFixed(1)}MB`,
      status: 'uploading',
      progress: Math.floor(Math.random() * 50) + 30
    }));
    setUploadedFiles(prev => [...prev, ...newFiles]);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const getFileStatusIcon = (file) => {
    if (file.status === 'completed') {
      return <CompletedIcon sx={{ color: ht.green, fontSize: 20 }} />;
    } else if (file.status === 'error') {
      return <ErrorIcon sx={{ color: ht.red, fontSize: 20 }} />;
    }
    return null;
  };

  return (
    <Box sx={harmonateDashboardPageSx}>
      <Typography sx={{ ...harmonateDashboardTitleSx, mb: 2 }}>
        Accounts Payable Recovery
      </Typography>
      
      {/* Recovery Table */}
      <TableContainer 
        component={Paper} 
        elevation={0} 
        sx={{ 
          ...harmonateCardSx,
          mb: 2,
          maxHeight: '28vh',
          bgcolor: ht.white,
          '& .MuiTableRow-root:nth-of-type(even)': {
            backgroundColor: ht.listBg,
          },
          '& .MuiTableRow-root:hover': {
            backgroundColor: ht.secondaryTab,
          },
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: ht.text, borderBottom: `1px solid ${ht.line}`, bgcolor: ht.white }}>Vendor</TableCell>
              <TableCell sx={{ fontWeight: 700, color: ht.text, borderBottom: `1px solid ${ht.line}`, bgcolor: ht.white }}>Invoices</TableCell>
              <TableCell sx={{ fontWeight: 700, color: ht.text, borderBottom: `1px solid ${ht.line}`, bgcolor: ht.white }}>Amount</TableCell>
              <TableCell sx={{ fontWeight: 700, color: ht.text, borderBottom: `1px solid ${ht.line}`, bgcolor: ht.white }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recoveryData.map((row, index) => (
              <TableRow key={index} hover sx={{ '& td': { borderBottom: `1px solid ${ht.line}`, color: ht.text } }}>
                <TableCell>{row.vendor}</TableCell>
                <TableCell>{row.invoices}</TableCell>
                <TableCell>{row.amount.toLocaleString()}</TableCell>
                <TableCell>{getStatusIcon(row.status)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Insights Header */}
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: ht.white }}>
        Insights
      </Typography>
      
      {/* Insights Section */}
      <Grid container spacing={2} sx={{ mb: 1 }}>
        {/* Recent Uploads */}
        <Grid item xs={12} md={2.5}>
          <Card elevation={0} sx={{ ...harmonateKpiCardSx, height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: ht.text, fontWeight: 700 }}>
                Recent Uploads
              </Typography>
              <List dense disablePadding>
                {recentUploads.map((item, index) => (
                  <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                    <ListItemText 
                      primary={item.file} 
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>  
          </Card>
        </Grid>
        
        {/* Monthly Recoveries */}
        <Grid item xs={12} md={2.5}>
          <Card elevation={0} sx={{ ...harmonateKpiCardSx, height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: ht.text, fontWeight: 700 }}>
                Monthly Recoveries
              </Typography>
              <Box sx={{ height: 120 }}>
                <Bar data={monthlyRecoveriesData} options={chartOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Pie Chart */}
        <Grid item xs={12} md={2.5}>
          <Card elevation={0} sx={{ ...harmonateKpiCardSx, height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: ht.text, fontWeight: 700 }}>
                Invoice % of Fraud
              </Typography>
              <Box sx={{ display: 'flex', height: 120, alignItems: 'center' }}>
                <Box sx={{ width: '50%', height: '100%' }}>
                  <Pie data={pieChartData} options={pieOptions} />
                </Box>
                <Box sx={{ width: '50%', pl: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: ht.green, mr: 1 }} />
                    <Typography variant="caption" sx={{ color: ht.green }}>Invoice</Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ ml: 'auto' }}>75%</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: ht.blue, mr: 1 }} />
                    <Typography variant="caption" sx={{ color: ht.text }}>Fraud</Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ ml: 'auto' }}>25%</Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Statistics Section */}
        <Grid item xs={12} md={4.5}>
          <Card elevation={0} sx={{ ...harmonateKpiCardSx, height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: ht.fadedText }}>This month</Typography>
                  <Typography variant="h6" sx={{ color: ht.green, fontWeight: 'bold' }}>$37.5K</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: ht.green, mr: 0.5 }}>+6.4%</Typography>
                    <Typography variant="caption" sx={{ color: ht.fadedText }}>Total Fraud</Typography>
                  </Box>
                </Box>
                <Chip 
                  label="Insight" 
                  size="small" 
                  sx={{ 
                    bgcolor: ht.green, 
                    color: ht.white,
                    height: 24
                  }} 
                />
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: ht.green, mr: 1 }} />
                  <Typography variant="caption" sx={{ color: ht.text }}>On track</Typography>
                </Box>
              </Box>
              
              <Box sx={{ height: 100 }}>
                <Line
                  data={{
                    labels: ['SEP', 'OCT', 'NOV', 'DEC', 'JAN', 'FEB'],
                    datasets: [
                      {
                        data: [25, 40, 30, 45, 35, 55],
                        borderColor: ht.green,
                        backgroundColor: 'transparent',
                        tension: 0.4,
                        pointRadius: 3,
                        pointBackgroundColor: ht.green
                      },
                      {
                        data: [20, 35, 25, 40, 30, 45],
                        borderColor: ht.blue,
                        backgroundColor: 'transparent',
                        tension: 0.4,
                        pointRadius: 3,
                        pointBackgroundColor: ht.blue
                      }
                    ]
                  }} 
                  options={lineChartOptions}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bottom Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
        <Button 
          variant="contained" 
          disableElevation
          onClick={() => setUploadModalOpen(true)}
          sx={{ 
            ...harmonateProceedButtonSx,
            py: 0.55,
            width: '32%',
          }}
        >
          New File Upload
        </Button>
        
        <Button 
          variant="contained" 
          disableElevation
          sx={{ 
            ...harmonateProceedButtonSx,
            py: 0.55,
            width: '32%',
          }}
        >
          Download Dashboard
        </Button>
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          bgcolor: ht.white, 
          borderRadius: '20px', 
          boxShadow: ht.cardShadow,
          p: 0.55, 
          width: '15%',
          border: `1px solid ${ht.line}`,
        }}>
          <Typography variant="subtitle2" sx={{ color: ht.text }}>Q2 2025</Typography>
          <KeyboardArrowUpIcon sx={{ color: ht.text }} />
        </Box>
      </Box>

      {/* File Upload Modal */}
      <Dialog 
        open={uploadModalOpen} 
        onClose={() => setUploadModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          ...harmonateDialogPaperProps,
          sx: {
            ...harmonateDialogPaperProps.sx,
            maxHeight: '85vh',
            width: '90%',
            maxWidth: '1000px',
          },
        }}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: ht.white }}>File Upload</Typography>
            <Typography variant="body2" sx={{ color: ht.white }}>Add your documents here</Typography>
          </Box>
          <IconButton onClick={() => setUploadModalOpen(false)} size="small" sx={harmonateDialogCloseIconSx}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={harmonateDialogContentSx}>
          <Grid container spacing={3}>
            {/* Left Side - Upload Area */}
            <Grid item xs={12} md={6}>
              {/* Drag and Drop Area */}
              <Box
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                sx={{
                  border: `2px dashed ${ht.line}`,
                  borderRadius: ht.cardRadius,
                  p: 4,
                  textAlign: 'center',
                  mb: 3,
                  bgcolor: ht.listBg,
                  cursor: 'pointer',
                  minHeight: 200,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    bgcolor: ht.secondaryTab,
                    borderColor: ht.green
                  }
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 48, color: ht.fadedText, mb: 2 }} />
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 500, color: ht.text }}>
                  Drag your file(s) to start uploading
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: ht.fadedText }}>
                  or
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  sx={harmonateUploadOutlinedSx}
                >
                  Browse files
                  <input
                    type="file"
                    hidden
                    multiple
                    onChange={handleFileUpload}
                    accept=".jpg,.png,.pdf,.xlsx"
                  />
                </Button>
              </Box>

              <Typography variant="caption" sx={{ color: ht.fadedText, display: 'block', textAlign: 'center' }}>
                Supports .jpg, .png, .pdf and .xlsx
              </Typography>

              {/* Uploaded Files List */}
              {uploadedFiles.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  {uploadedFiles.map((file, index) => (
                    <Box key={index}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        py: 2
                      }}>
                        <Box sx={{ flex: 1, mr: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                            {file.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {file.size}
                          </Typography>
                          {file.status === 'uploading' && (
                            <Box sx={{ mt: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={file.progress} 
                                sx={{ 
                                  height: 4, 
                                  borderRadius: 2,
                                  bgcolor: ht.line,
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: ht.green
                                  }
                                }} 
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                Uploading...
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {file.status === 'uploading' && (
                            <>
                              <ErrorIcon sx={{ color: ht.red, fontSize: 16, mr: 1 }} />
                              <ErrorIcon sx={{ color: ht.red, fontSize: 16 }} />
                            </>
                          )}
                          {getFileStatusIcon(file)}
                        </Box>
                      </Box>
                      {index < uploadedFiles.length - 1 && <Divider sx={{ borderColor: ht.line }} />}
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>

            {/* Right Side - Mapping Section */}
            <Grid item xs={12} md={6}>
              <Box sx={{ pl: { md: 2 } }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        Account Number
                      </Typography>
                      <IconButton size="small" sx={{ ml: 1, p: 0.5 }}>
                        <HelpIcon fontSize="small" sx={{ color: ht.fadedText }} />
                      </IconButton>
                    </Box>
                    <FormControl fullWidth size="small">
                      <Select 
                        value={mappingValues.accountNumber}
                        onChange={(e) => setMappingValues(prev => ({...prev, accountNumber: e.target.value}))}
                        MenuProps={harmonateSelectMenuProps}
                        sx={{ bgcolor: ht.white, ...harmonateFormFieldsSx }}
                      >
                        <MenuItem value="Auto">Auto</MenuItem>
                        <MenuItem value="Manual">Manual</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        Contract
                      </Typography>
                      <IconButton size="small" sx={{ ml: 1, p: 0.5 }}>
                        <HelpIcon fontSize="small" sx={{ color: ht.fadedText }} />
                      </IconButton>
                    </Box>
                    <FormControl fullWidth size="small">
                      <Select 
                        value={mappingValues.contract}
                        onChange={(e) => setMappingValues(prev => ({...prev, contract: e.target.value}))}
                        MenuProps={harmonateSelectMenuProps}
                        sx={{ bgcolor: ht.white, ...harmonateFormFieldsSx }}
                      >
                        <MenuItem value="Auto">Auto</MenuItem>
                        <MenuItem value="Manual">Manual</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        General Ledger
                      </Typography>
                      <IconButton size="small" sx={{ ml: 1, p: 0.5 }}>
                        <HelpIcon fontSize="small" sx={{ color: ht.fadedText }} />
                      </IconButton>
                    </Box>
                    <FormControl fullWidth size="small">
                      <Select 
                        value={mappingValues.generalLedger}
                        onChange={(e) => setMappingValues(prev => ({...prev, generalLedger: e.target.value}))}
                        MenuProps={harmonateSelectMenuProps}
                        sx={{ bgcolor: ht.white, ...harmonateFormFieldsSx }}
                      >
                        <MenuItem value="NetSuite">NetSuite</MenuItem>
                        <MenuItem value="SAP">SAP</MenuItem>
                        <MenuItem value="QuickBooks">QuickBooks</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 3, mt: 3, borderTop: `1px solid ${ht.line}` }}>
            <Button 
              variant="outlined" 
              onClick={() => setUploadModalOpen(false)}
              sx={harmonateCancelButtonSx}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              disableElevation
              sx={harmonateProceedButtonSx}
            >
              Upload
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AccountsPayableRecovery;